import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Layers } from "lucide-react";
import { Section, SectionHeader } from "./shared";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import type { SolutionData } from "@/data/solutionsData";
import { getCategoryColor } from "@/lib/categoryColors";

const UNIFORM_COLOR = { border: "var(--primary)", accent: "var(--primary)" };

const getCardColor = (card: { category?: string }) => {
  if (card.category) {
    const c = getCategoryColor(card.category);
    return { border: c.text, accent: c.text };
  }
  return UNIFORM_COLOR;
};

interface Props {
  solution: SolutionData;
  onCtaClick: () => void;
}

const HIDDEN_SLUGS = ["landing-in-europe", "emerging-biotech", "manufacturing-quality-compliance"];

const SolutionHelpCards = ({ solution, onCtaClick }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);

  if (!solution.helpCards || HIDDEN_SLUGS.includes(solution.slug)) return null;

  const cards = solution.helpCards.cards;

  return (
    <Section bg={!solution.whyQbd}>
      <div className="mb-10">
        <SectionHeader label={({ en: solution.category === "pharma" ? "Pharma Operations" : "Device Operations", es: solution.category === "pharma" ? "Operaciones farmacéuticas" : "Operaciones de dispositivos", "zh-cn": solution.category === "pharma" ? "制药运营支持" : "器械运营支持" }[lang])} title={solution.helpCards.title} icon={Layers} />
      </div>

      <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10 leading-relaxed">
        {solution.helpCards.subtitle}
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        {cards.map((card, i) => {
          const colors = getCardColor(card);
          return (
            <motion.div
              key={i}
              className="bg-card border border-border rounded-xl overflow-hidden group cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5, scale: 1.02 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              onClick={() => solution.hubspotFormId && onCtaClick()}
            >
              {card.image && (
                <div className="aspect-[12/5] overflow-hidden relative">
                  <motion.img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                  <div className="absolute inset-0 bg-primary/25 mix-blend-multiply transition-opacity duration-300 group-hover:opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                    <span className="text-primary-foreground text-sm font-medium flex items-center gap-1.5">
                      {t("learnMore")} <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              )}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-foreground mb-1">{card.title}</h3>
                <p className="text-sm font-medium mb-4" style={{ color: colors.accent }}>{card.tagline}</p>
                <ul className="space-y-2">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: colors.border }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          );
        })}
      </div>

      {solution.hubspotFormId && (
        <div className="text-center mt-12">
          <Button className="rounded-full px-8" onClick={onCtaClick}>
            {t("discussProject")} <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      )}
    </Section>
  );
};

export default SolutionHelpCards;
